import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight, CheckCircle2, Wrench } from 'lucide-react';
import { useApp } from '@/store';
import { useT, type I18nKey } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type UpdateItem = {
  key: I18nKey;
  route: (firstId: string) => string;
  selector?: string;
};

type Section = {
  titleKey: I18nKey;
  items: UpdateItem[];
};

// ── Tab 1: Client feedback (PDF review changes) ──────────────────────────────
const FEEDBACK_SECTIONS: Section[] = [
  {
    titleKey: 'updates.section.customerDetail',
    items: [
      {
        key: 'updates.detail.contractToggle',
        route: id => (id ? `/customer/${id}/info` : '/overview'),
        selector: '[data-focus="service-contract"]',
      },
      {
        key: 'updates.detail.contractPdfGated',
        route: id => (id ? `/customer/${id}/info` : '/overview'),
        selector: '[data-focus="service-contract"]',
      },
      {
        key: 'updates.detail.linkedCustomers',
        route: id => (id ? `/customer/${id}/info` : '/overview'),
        selector: '[data-focus="linked-customers"]',
      },
    ],
  },
  {
    titleKey: 'updates.section.extStatus',
    items: [
      {
        key: 'updates.ext.statusCol',
        route: id => (id ? `/customer/${id}/extinguishers` : '/overview'),
      },
      {
        key: 'updates.ext.sortHeaders',
        route: id => (id ? `/customer/${id}/extinguishers` : '/overview'),
      },
    ],
  },
];

// ── Tab 2: Stale → fixed changes ─────────────────────────────────────────────
const CHANGES_SECTIONS: Section[] = [];

const allItems = (sections: Section[]) => sections.flatMap(s => s.items);
const TOTAL_FEEDBACK = allItems(FEEDBACK_SECTIONS).length;
const TOTAL_CHANGES = allItems(CHANGES_SECTIONS).length;

export function UpdatesPanel() {
  const t = useT();
  const nav = useNavigate();
  const firstId = useApp(s => s.customers[0]?.id ?? '');
  const updatesSeen = useApp(s => s.updatesSeen);
  const visitedUpdates = useApp(s => s.visitedUpdates);
  const markUpdatesSeen = useApp(s => s.markUpdatesSeen);
  const markUpdateItemVisited = useApp(s => s.markUpdateItemVisited);
  const setFocus = useApp(s => s.setFocus);

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'feedback' | 'changes'>(
    'feedback'
  );

  const unvisitedFeedback = allItems(FEEDBACK_SECTIONS).filter(
    i => !visitedUpdates.includes(i.key)
  ).length;
  const unvisitedChanges = allItems(CHANGES_SECTIONS).filter(
    i => !visitedUpdates.includes(i.key)
  ).length;
  const totalUnvisited = unvisitedFeedback + unvisitedChanges;

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (v && !updatesSeen) markUpdatesSeen();
  };

  const handleItemClick = (item: UpdateItem) => {
    markUpdateItemVisited(item.key);
    setOpen(false);
    nav(item.route(firstId));
    if (item.selector) {
      setTimeout(() => setFocus(item.selector!, t(item.key)), 50);
    }
  };

  const renderSections = (sections: Section[]) =>
    sections.map(sec => (
      <div key={sec.titleKey} className="py-1">
        <div className="px-4 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t(sec.titleKey)}
          </span>
        </div>
        {sec.items.map(item => {
          const visited = visitedUpdates.includes(item.key);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleItemClick(item)}
              className="w-full flex items-start gap-3 px-4 py-2 text-left hover:bg-accent transition-colors"
            >
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
              <span
                className={`flex-1 text-[12px] leading-snug ${visited ? 'text-muted-foreground' : ''}`}
              >
                {t(item.key)}
              </span>
              {!visited && (
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-destructive" />
              )}
              {visited && item.selector && (
                <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/30" />
              )}
            </button>
          );
        })}
      </div>
    ));

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative gap-1.5 h-8 rounded-full px-2 sm:px-3 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-950/40 dark:hover:text-amber-400"
          aria-label={t('updates.title')}
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span className="hidden sm:inline text-xs font-medium">
            {t('updates.new')}
          </span>
          {totalUnvisited > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
              {totalUnvisited}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-72 sm:w-80 max-h-screen overflow-y-auto p-0"
        sideOffset={8}
      >
        {/* Header */}
        <div className="border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="font-semibold text-sm">{t('updates.title')}</span>
            {totalUnvisited > 0 && (
              <span className="ml-auto rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground">
                {totalUnvisited}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setActiveTab('feedback')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold transition-colors ${
              activeTab === 'feedback'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Sparkles className="h-3 w-3" />
            {t('updates.tab.feedback')}
            {unvisitedFeedback > 0 && (
              <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[9px] font-bold text-destructive-foreground">
                {unvisitedFeedback}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('changes')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold transition-colors ${
              activeTab === 'changes'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Wrench className="h-3 w-3" />
            {t('updates.tab.changes')}
            {unvisitedChanges > 0 && (
              <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[9px] font-bold text-destructive-foreground">
                {unvisitedChanges}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'feedback'
          ? renderSections(FEEDBACK_SECTIONS)
          : renderSections(CHANGES_SECTIONS)}

        {/* Footer */}
        <div className="border-t px-4 py-2 text-[10px] text-muted-foreground flex justify-end">
          <span>
            {activeTab === 'feedback' ? TOTAL_FEEDBACK : TOTAL_CHANGES}{' '}
            {t(
              activeTab === 'feedback'
                ? 'updates.tab.feedback'
                : 'updates.tab.changes'
            )}
          </span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
